// frontend/src/pages/SavedArticlesPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getSavedArticles } from '../services/api';
import ArticleCard from '../components/Dashboard/ArticleCard';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import useAuth from '../hooks/useAuth';
import { Link } from 'react-router-dom';
import { BookmarkIcon as BookmarkOutlineIcon } from '@heroicons/react/24/outline'; // Added this import

const SavedArticlesPage = () => {
  const [savedArticleEntries, setSavedArticleEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const observerTarget = useRef(null);

  const fetchSaved = useCallback(async (page = 1, append = false) => {
    if (!isAuthenticated) return;

    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setSavedArticleEntries([]);
    }

    setError('');
    try {
      const data = await getSavedArticles(page, 12);
      // data structure: { count, next, previous, results: [...] }
      const newEntries = data.results || [];

      if (append) {
        setSavedArticleEntries(prev => [...prev, ...newEntries]);
      } else {
        setSavedArticleEntries(newEntries);
      }

      // Check if there are more pages
      setHasMore(!!data.next);

    } catch (err) {
      setError(err.message || 'Failed to fetch saved articles.');
      if (!append) {
        setSavedArticleEntries([]);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [isAuthenticated]);

  // Initial load
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      setCurrentPage(1);
      fetchSaved(1, false);
    }
  }, [authLoading, isAuthenticated]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!hasMore || isLoading || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          const nextPage = currentPage + 1;
          setCurrentPage(nextPage);
          fetchSaved(nextPage, true);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, isLoading, isLoadingMore, currentPage, fetchSaved]);

  const handleSaveToggleInSavedPage = (articleId, isNowSaved) => {
    if (!isNowSaved) {
      setSavedArticleEntries(prevEntries =>
        prevEntries.filter(entry => entry.article.id !== articleId)
      );
    }
    // Optionally, refetch or handle adding back if re-saved,
    // but for this page, removal on unsave is the primary concern.
  };

  if (authLoading) {
    return (
      <div className="flex justify-center items-center flex-grow p-10">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
        <div className="text-center py-20">
            <p className="text-xl text-gray-700 mb-4">Please log in to view your saved articles.</p>
            <Link to="/login" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg">
                Log In
            </Link>
        </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-8">Your Saved Articles</h1>
      {isLoading && savedArticleEntries.length === 0 && <div className="py-10"><LoadingSpinner /></div>}
      {error && <p className="text-red-500 bg-red-100 p-3 rounded-md text-center my-6">Error: {error}</p>}
      
      {!isLoading && !error && savedArticleEntries.length === 0 && (
        <div className="text-center py-10">
            <BookmarkOutlineIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-xl text-gray-600 mb-2">No articles saved yet.</p>
            <p className="text-gray-500 mb-6">Articles you save for later will appear here.</p>
            <Link to="/article-feed" className="text-blue-600 hover:text-blue-700 font-semibold">
                Explore Articles &rarr;
            </Link>
        </div>
      )}

      {savedArticleEntries.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
            {savedArticleEntries.map((savedEntry) => {
              // The SavedArticleSerializer embeds the full Article object in 'article'
              // and ArticleSerializer includes 'is_saved'. We pass this down.
              // For saved articles page, 'is_saved' should always be true initially.
              const articleWithSaveStatus = { ...savedEntry.article, is_saved: true };
              return (
                  <ArticleCard
                  key={savedEntry.article.id} // Use the actual article's ID
                  article={articleWithSaveStatus}
                  onSaveToggle={handleSaveToggleInSavedPage}
                  />
              );
          })}
          </div>

          {/* Infinite scroll trigger element */}
          <div ref={observerTarget} className="w-full py-8 flex justify-center">
            {isLoadingMore && <LoadingSpinner />}
          </div>

          {!hasMore && savedArticleEntries.length > 0 && (
            <p className="text-center text-gray-500 py-4">No more saved articles to load.</p>
          )}
        </>
      )}
    </div>
  );
};

export default SavedArticlesPage;
